import { getProppingBounds } from '../../products/models';
import tacHelpers from '../../../state/tac/tacHelpers';
import productService from '../../products';
import {
  calcAvailableSpaceInParent,
  calcAvailableSpaceInScene,
  getLargestPropping,
  getRandomPropping,
  replaceMovingItem,
} from '../common';
import geometry from '../../../scene/util/geometry';
import { ITEMS } from '../../../constants';

function getClosestItemBelow(items, omit = {}) {
  return items
    .filter(
      item =>
        (!item.itemid || item.itemid !== omit.itemid) &&
        item.y < omit.y &&
        item.y > 0
    )
    .sort((a, b) => b.y - a.y)
    .map(item => item.y + item.height)[0];
}

function pickPropping({
  isDynamic,
  tac,
  item,
  parentItem,
  section,
  movingItem,
  movingSprite,
}) {
  if (!isDynamic) {
    return getRandomPropping(item);
  }

  const proppingBounds = getProppingBounds(item);

  if (!parentItem || !parentItem.itemid) {
    // Parent is Scene, return default bounds.
    return proppingBounds[0];
  }

  let space;
  let closest;
  let myPos = item.y;

  if (section) {
    closest = getClosestItemBelow(
      section.childrenContainer.children.map(child => child.item),
      { ...item, y: parentItem.y + item.y }
    );

    if (closest) {
      // We found something, adjust item values to be relative to the section instead
      myPos += parentItem.y;
    }
  }

  function createMeasuringArea(sizingItem, globalCoords, parentItem) {
    const postWidth = productService.getPostWidth();

    // To find out if there are items under the clothes-rail we need
    // to create a rect that we use with getCollidingRects()
    const measuringArea = Object.assign({}, sizingItem, globalCoords);

    // The rect needs to stretch from the floor up to the clothes-rails y position
    measuringArea.height = measuringArea.y;
    measuringArea.y = 0;

    // It should stretch all the way back to wall to not block items placed behind
    const oldZ = measuringArea.z;
    measuringArea.z = 0;
    measuringArea.depth += oldZ;

    // Make it half a post width narrower on each side since we don't care about,
    // collisions with our own posts
    measuringArea.x += postWidth / 2;
    measuringArea.width -= postWidth;

    return measuringArea;
  }

  if (closest) {
    space = calcAvailableSpaceInParent(myPos, closest);
  } else {
    // No collisions found inside section, let's try once more against all other items in scene
    const tacParentItem = tacHelpers.getItem(tac, parentItem.itemid);
    const globalParent = tacHelpers.getGlobalCoords(tacParentItem, tac);
    const globalItem = {
      x: globalParent.x + item.x,
      y: globalParent.y + item.y,
      z: globalParent.z + item.z,
    };

    const rects = tacHelpers
      .getRects(tac, item, tac, true)
      // We don't care about collisions with sections or brackets
      .filter(rect => !productService.isType(rect, [ITEMS.SECTION, 'bracket']));

    if (movingItem) {
      const movingFullSize = {
        ...movingItem,
        ...tacHelpers.getFullSize(movingItem),
      };

      const globalMoving = {
        ...movingFullSize,
      };

      if (movingSprite?.parentItem?.itemid) {
        const parentOfMoving = tacHelpers.getGlobalCoords(
          movingSprite.parentItem,
          tac
        );

        globalMoving.x += parentOfMoving.x;
        globalMoving.y += parentOfMoving.y;
        globalMoving.z += parentOfMoving.z;
      }

      const newRect = tacHelpers.getRects({ items: [globalMoving] })[0];

      replaceMovingItem(rects, newRect);
    }

    const measuringArea = createMeasuringArea(item, globalItem, parentItem);

    const collidingRects = geometry.getCollidingRects(measuringArea, rects);

    space = calcAvailableSpaceInScene(measuringArea.height, collidingRects);
  }

  const largestFittingPropping = getLargestPropping(proppingBounds, space, 50);

  // Fallback to default bounds
  return largestFittingPropping || proppingBounds[0];
}

export default { pickPropping };
